import React, { memo, useCallback, useMemo, useState } from "react";
import FastImage from "react-native-fast-image";
import { IFile } from "@microdoing/shared/services/userCampaign/types";
import { ResponsiveValue } from "@shopify/restyle";
import { Theme } from "app/Theme/Theme";
import { Box } from "../Box";
import { useDispatch } from "react-redux";
import { requestExternalUser } from "@microdoing/shared/services/externalProfile/actions";
import normalize from "app/utils/normalize";
import { PressableOpacity } from "../PressableOpacity";
import CommentaryContent from "./CommentaryContent";
import CommentaryHeader from "./CommentaryHeader";
import CommentaryResponses from "./CommentaryResponses";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import ResponseInputWeb from "./ResponseInputWeb";

export type CommentaryType = {
  _id: string;
  like: {
    users: string[];
    numberOfLike: number;
  };
  date: string;
  responses: CommentaryType[];
  user: {
    _id: string;
    picture: string;
    firstName: string;
    lastName: string;
  };
  comment: string;
  stepId: string;
  reports: [
    {
      _id: string;
      type: string;
      user: string;
      comment: string;
    },
  ];
  commentId: string;
  fileId: IFile;
  createdAt: string;
  updatedAt: string;
};

export interface ICommentariesProps {
  commentary: CommentaryType;
  isLastComment?: boolean;
  scrollToBottom?: () => void;
  marginTop: ResponsiveValue<keyof Theme["spacing"], Theme>;
  handleResponse?: ({
    stepCommentId,
    isModifier,
    comment,
    receiver,
  }) => () => void;
}

const Commentary = ({
  commentary,
  marginTop = "null",
  isLastComment = false,
  scrollToBottom = () => null,
  handleResponse = ({}) => () => {},
}) => {
  const [receiver, setReceiver] = useState({ _id: "", firstName: "" });
  const [isModifier, setIsModifier] = useState(false);
  const [stepCommentId, setStepCommentId] = useState("");
  const [width, setWidth] = useState(300);

  const dispatch = useDispatch();

  const handleSeeUser = useCallback(() => {
    dispatch(requestExternalUser({ userId: commentary?.user?._id }));
  }, [commentary?.user?._id]);

  const avatarSource = useMemo(() => {
    return {
      uri: commentary?.user?.picture,
    };
  }, [commentary?.user?.picture]);

  const avatarSize = useMemo(() => {
    return {
      marginTop: commentary.commentId ? normalize(2.5) : normalize(-1.5),
      height: commentary.commentId ? normalize(18) : normalize(24),
      width: commentary.commentId ? normalize(18) : normalize(24),
      borderRadius: normalize(25),
    };
  }, [commentary?.commentId]);

  const handleResponseFunc = useCallback(() => {
    return handleResponse({
      stepCommentId: commentary._id,
      isModifier: true,
      comment: commentary.comment,
      receiver: commentary?.user,
    })();
  }, [commentary?._id, commentary?.comment, commentary?.user]);

  const response = ({ stepCommentId, isModifier, comment, receiver }) => () => {
    resetState();
    setTimeout(() => {
      setReceiver(receiver);
      setIsModifier(isModifier);
      setStepCommentId(stepCommentId);
      handleResponse({
        stepCommentId,
        isModifier,
        comment,
        receiver,
      })();
    }, 10);
  };

  const resetState = () => {
    setReceiver({ _id: "", firstName: "" });
    setIsModifier(false);
    setStepCommentId("");
  };

  const findDimensions = ({ width }) => {
    setWidth(width);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  return (
    <Box flex={1} marginTop={marginTop} flexDirection="row">
      <Box flexDirection="column">
        <PressableOpacity onPress={handleSeeUser}>
          <FastImage
            style={avatarSize}
            source={avatarSource}
            resizeMode="cover"
          />
        </PressableOpacity>
        {!!(commentary.responses.length > 0) && (
          <Box
            margin="s"
            alignSelf="center"
            borderWidth={0.5}
            width={0.5}
            flex={1}
            borderColor="neutral.300"
          />
        )}
      </Box>
      <Box
        flexDirection="column"
        marginLeft="xs"
        flex={1}
        onLayout={handleOnLayout}
      >
        <CommentaryHeader
          commentary={commentary}
          handleResponse={handleResponseFunc}
        />
        <CommentaryContent
          commentaryWidth={width}
          commentary={commentary}
          handleResponse={response}
        />
        <CommentaryResponses
          isLastComment={isLastComment}
          scrollToBottom={scrollToBottom}
          commentary={commentary}
          handleResponse={response}
        />
        {isWebDesktop &&
          commentary._id === stepCommentId &&
          receiver._id !== "" && (
            <ResponseInputWeb
              receiver={receiver}
              stepCommentId={stepCommentId}
              isModifier={isModifier}
              resetState={resetState}
            />
          )}
      </Box>
    </Box>
  );
};

export default memo(Commentary);
